import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { api, errMsg } from "../lib/api";

const TEXT = {
  en: { age: "I confirm I am 18 or older", data: "I consent to {name} processing my wellness information (such as assessment answers, check-ins, coach chats and posts) to provide this service, as described in the", policy: "Privacy Policy", assess: "I am 18+ and consent to my answers being processed to show my results, as described in the" },
  hi: { age: "मैं पुष्टि करता हूँ कि मेरी उम्र 18 वर्ष या उससे अधिक है", data: "मैं {name} को यह सेवा देने के लिए मेरी वेलनेस जानकारी (जैसे असेसमेंट के जवाब, चेक-इन, कोच चैट और पोस्ट) प्रोसेस करने की सहमति देता हूँ, जैसा कि इसमें बताया गया है:", policy: "गोपनीयता नीति", assess: "मेरी उम्र 18+ है और मैं अपने नतीजे देखने के लिए अपने जवाब प्रोसेस करने की सहमति देता हूँ, जैसा कि इसमें बताया गया है:" },
  hinglish: { age: "Main confirm karta hoon ki meri age 18 ya usse zyada hai", data: "Main {name} ko yeh service dene ke liye meri wellness info (jaise assessment answers, check-ins, coach chats aur posts) process karne ki consent deta hoon, jaisa yahan bataya gaya hai:", policy: "Privacy Policy", assess: "Meri age 18+ hai aur main apne results dekhne ke liye apne answers process karne ki consent deta hoon, jaisa yahan bataya gaya hai:" },
};

export function useConsentText() {
  const { lang } = useI18n();
  return TEXT[lang] || TEXT.en;
}

const ADULT = { en: "CalmKiBaat is only for adults (18+).", hi: "CalmKiBaat केवल वयस्कों (18+) के लिए है।", hinglish: "CalmKiBaat sirf adults (18+) ke liye hai." };

export function useAdultOnly() {
  const { lang } = useI18n();
  return ADULT[lang] || ADULT.en;
}

export function AgeCheck({ checked, onChange, testId, error }) {
  const c = useConsentText();
  return (
    <div className="space-y-2">
      <label className="flex items-start gap-3 rounded-2xl border border-ckb-line bg-white p-4 text-sm"><Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testId} aria-required="true" /><span>{c.age}</span></label>
      {error && <p className="text-sm text-[#C04A4A]" role="alert" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}

export function ConsentChecks({ value, onChange, prefix }) {
  const c = useConsentText();
  return (
    <div className="space-y-3 rounded-2xl border border-ckb-line bg-white p-4 text-sm">
      <label className="flex items-start gap-3"><Checkbox checked={value.consent_age} onCheckedChange={(v) => onChange({ ...value, consent_age: !!v })} data-testid={`${prefix}-consent-age`} aria-required="true" /><span>{c.age}</span></label>
      <label className="flex items-start gap-3"><Checkbox checked={value.consent_data} onCheckedChange={(v) => onChange({ ...value, consent_data: !!v })} data-testid={`${prefix}-consent-data`} aria-required="true" /><span>{c.data.replace("{name}", "CalmKiBaat")} <Link to="/privacy" target="_blank" className="text-ckb-forest underline">{c.policy}</Link>.</span></label>
    </div>
  );
}

// Blocks signed-in users (e.g. Google sign-ups, older accounts) until they give the current consent.
export function ConsentGate() {
  const { user, setUser, logout } = useAuth();
  const [v, setV] = useState({ consent_age: false, consent_data: false });
  const [busy, setBusy] = useState(false);
  if (!user?.consent_required) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/consent", v); setUser(data.user); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open>
      <DialogContent className="max-h-[90vh] overflow-y-auto [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="consent-gate">
        <DialogHeader><DialogTitle>Before you continue</DialogTitle><DialogDescription>Indian data-protection law needs your clear consent before we process wellness information.</DialogDescription></DialogHeader>
        <ConsentChecks value={v} onChange={setV} prefix="gate" />
        <div className="flex flex-wrap gap-2">
          <button className="ckb-btn-primary" disabled={busy || !v.consent_age || !v.consent_data} onClick={save} data-testid="consent-gate-accept">I agree, continue</button>
          <button className="ckb-btn-ghost" onClick={logout} data-testid="consent-gate-decline">No, sign me out</button>
        </div>
        <p className="text-xs text-ckb-muted">You can withdraw consent any time by deleting your account in Profile → Data controls.</p>
      </DialogContent>
    </Dialog>
  );
}
